/**
 * The tooltip.
 *
 * Built rather than borrowed from `title` because the native one cannot do
 * three things this app needs: it does not appear on keyboard focus in every
 * browser, it cannot be reached on a phone at all, and its timing is the
 * operating system's rather than the brief's.
 *
 * What it does:
 *
 *   - Waits for intentional hover. A tooltip that fires the instant a pointer
 *     crosses an icon is noise, and people learn to ignore noise. Once one is
 *     open, moving to a neighbouring target opens that one almost immediately,
 *     because by then the intent is established.
 *
 *   - Appears on focus with no delay at all. Someone tabbing to a control has
 *     already declared their intent; making them wait is just slower.
 *
 *   - Opens on tap on touch devices, and closes on the next tap anywhere. Icon
 *     buttons carry meaning that phone users would otherwise never get.
 *
 *   - Stays inside the viewport, flipping above/below and clamping sideways,
 *     with the arrow tracking the target rather than the box.
 *
 *   - Does not flicker when the pointer moves from the target onto the tooltip
 *     itself, which is what makes a tooltip with a link or a long path in it
 *     usable rather than infuriating.
 *
 * It is deliberately not a general popover: no focus trap, no interactive
 * content beyond selectable text. Anything that needs a click belongs in a
 * menu.
 */

import {
  useCallback, useEffect, useId, useLayoutEffect, useRef, useState,
  type ReactNode
} from 'react';
import { createPortal } from 'react-dom';

type Side = 'top' | 'bottom' | 'right';

export type TooltipProps = {
  /** The text. Kept short — anything long enough to need a paragraph belongs
      in visible helper text, not behind a hover. */
  content: ReactNode;
  children: ReactNode;
  /** Where it prefers to sit. It will flip if there is no room. */
  side?: Side;
  /** Suppress without unmounting the child, for the common case of a tooltip
      that only applies in one state. */
  disabled?: boolean;
  /** Extra classes for the wrapper, which is an inline-flex span. */
  className?: string;
};

/* Shared across every instance: the "one is already open" state that makes
   moving between neighbouring targets feel immediate instead of stuttery. */
let warm = false;
let warmTimer: number | undefined;

const GAP = 8;        /* between target and tooltip */
const EDGE = 8;       /* minimum distance from the viewport edge */

export function Tooltip({ content, children, side = 'top', disabled, className }: TooltipProps) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ x: number; y: number; side: Side; arrow: number } | null>(null);
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const enterTimer = useRef<number | undefined>(undefined);
  const leaveTimer = useRef<number | undefined>(undefined);
  const id = useId();

  const clearTimers = () => {
    window.clearTimeout(enterTimer.current);
    window.clearTimeout(leaveTimer.current);
  };

  const show = useCallback((immediate: boolean) => {
    clearTimers();
    if (disabled) return;
    const delay = immediate ? 0 : warm ? 80 : 400;
    enterTimer.current = window.setTimeout(() => {
      setOpen(true);
      warm = true;
      window.clearTimeout(warmTimer);
    }, delay);
  }, [disabled]);

  const hide = useCallback(() => {
    clearTimers();
    /*
     * A short grace period before closing.
     *
     * Without it, moving the pointer from the target onto the tooltip crosses
     * the gap between them, leaves the target, and closes the thing you were
     * reaching for. The tooltip's own pointer handlers cancel this.
     */
    leaveTimer.current = window.setTimeout(() => {
      setOpen(false);
      /* Stay "warm" briefly so the next target opens fast, then cool off. */
      window.clearTimeout(warmTimer);
      warmTimer = window.setTimeout(() => { warm = false; }, 500);
    }, 90);
  }, []);

  useEffect(() => clearTimers, []);

  /*
   * Position after paint, from the real measured size.
   *
   * Guessing the width from the text length is close enough until somebody
   * writes a long file path into one, at which point it hangs off the screen.
   */
  useLayoutEffect(() => {
    if (!open) return;
    const anchor = anchorRef.current;
    const tip = tipRef.current;
    if (!anchor || !tip) return;

    const a = anchor.getBoundingClientRect();
    const t = tip.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;

    /* Beside the target, for a vertical rail: above or below would sit on top
       of the neighbouring icon. Falls back to above if there is no room. */
    if (side === 'right' && a.right + GAP + t.width <= vw - EDGE) {
      const ry = Math.min(Math.max(a.top + a.height / 2 - t.height / 2, EDGE), Math.max(EDGE, vh - t.height - EDGE));
      const rArrow = Math.min(Math.max(a.top + a.height / 2 - ry, 10), Math.max(10, t.height - 10));
      setCoords({ x: a.right + GAP, y: ry, side: 'right', arrow: rArrow });
      return;
    }

    /* Flip if the preferred side has no room and the other does. */
    let placed: Side = side === 'right' ? 'top' : side;
    if (side === 'top' && a.top - t.height - GAP < EDGE && a.bottom + t.height + GAP < vh - EDGE) placed = 'bottom';
    if (side === 'bottom' && a.bottom + t.height + GAP > vh - EDGE && a.top - t.height - GAP > EDGE) placed = 'top';

    const y = placed === 'top' ? a.top - t.height - GAP : a.bottom + GAP;

    /* Centre on the target, then clamp to the viewport. */
    const wanted = a.left + a.width / 2 - t.width / 2;
    const x = Math.min(Math.max(wanted, EDGE), Math.max(EDGE, vw - t.width - EDGE));

    /* The arrow follows the target, not the clamped box - otherwise a tooltip
       pushed away from an edge points at nothing. */
    const arrow = Math.min(Math.max(a.left + a.width / 2 - x, 12), Math.max(12, t.width - 12));

    setCoords({ x, y, side: placed, arrow });
  }, [open, side, content]);

  /* Scrolling or resizing moves the target out from under it. Closing is more
     honest than trying to chase it. */
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  /* Escape closes, as it does for every other transient surface here. */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { clearTimers(); setOpen(false); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  /* On touch, the next tap anywhere else dismisses. */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      if (anchorRef.current?.contains(e.target as Node)) return;
      clearTimers();
      setOpen(false);
    };
    window.addEventListener('pointerdown', onDown, true);
    return () => window.removeEventListener('pointerdown', onDown, true);
  }, [open]);

  if (disabled) return <>{children}</>;

  return (
    <>
      <span
        ref={anchorRef}
        className={className}
        style={{ display: 'inline-flex' }}
        /* Mouse and pen hover. Touch is handled below, because a touch device
           fires a synthetic pointerenter on tap that would otherwise open the
           tooltip and immediately have it dismissed by the same tap. */
        onPointerEnter={(e) => { if (e.pointerType !== 'touch') show(false); }}
        onPointerLeave={(e) => { if (e.pointerType !== 'touch') hide(); }}
        onPointerDown={(e) => {
          if (e.pointerType === 'touch') {
            clearTimers();
            setOpen((was) => !was);
          } else {
            /* Pressing the control means they have decided; the hint is in
               the way now. */
            clearTimers();
            setOpen(false);
          }
        }}
        onFocusCapture={() => show(true)}
        onBlurCapture={() => { clearTimers(); setOpen(false); }}
        aria-describedby={open ? id : undefined}
      >
        {children}
      </span>

      {open && createPortal(
        <div
          ref={tipRef}
          id={id}
          role="tooltip"
          onPointerEnter={clearTimers}
          onPointerLeave={hide}
          style={{
            position: 'fixed',
            left: coords ? coords.x : -9999,
            top: coords ? coords.y : -9999,
            zIndex: 2000,
            pointerEvents: 'auto',
            /* Hidden until measured, so it never flashes in the wrong place. */
            opacity: coords ? 1 : 0,
            transform: coords ? 'translateY(0)' : 'translateY(2px)',
            transition: 'opacity var(--t-tip-in) var(--ease), transform var(--t-tip-in) var(--ease)'
          }}
          className="max-w-[260px] rounded-[var(--radius)] border border-line bg-surface-4 px-2.5 py-1.5 text-[12.5px] leading-snug text-ink shadow-[var(--shadow-3)]"
        >
          {content}
          {coords && (
            <span
              aria-hidden
              className="absolute h-2 w-2 rotate-45 border-line bg-surface-4"
              style={{
                ...(coords.side === 'right'
                  ? { left: -5, top: coords.arrow - 4, borderLeft: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }
                  : { left: coords.arrow - 4 }),
                ...(coords.side === 'right' ? {} : coords.side === 'top'
                  ? { bottom: -5, borderRight: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }
                  : { top: -5, borderLeft: '1px solid var(--line)', borderTop: '1px solid var(--line)' })
              }}
            />
          )}
        </div>,
        document.body
      )}
    </>
  );
}
